'use client'

import { type DetailedHTMLProps, type HTMLAttributes, useState } from 'react'

import cn from 'clsx'
import type { Themes } from '@/Theme'

import { Text } from '@/components/Text/Text'
import { Checkbox } from '@doist/icons/brand'

import css from './stepper.module.css'
import { Box } from '@/components/Box/Box'

export interface StepperProps
    extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    steps: string[]
    numberOfSteps?: number
    theme?: Themes
    onStepSelected?: (step: string) => void
    showEnding?: boolean
    className?: string
}

export function Stepper({
    theme = 'todoist',
    steps = [],
    numberOfSteps = 0,
    onStepSelected,
    showEnding = false,
    className,
    ...rest
}: StepperProps) {
    const currentStep = steps.length - 1
    const allSteps = Array(numberOfSteps)
        .fill('')
        .map((_, i) => (i < steps.length ? steps[i] : `step-${i}`))
    const [tooltipIndex, setTooltipIndex] = useState(-1)
    const percentProgress = showEnding
        ? 100.0
        : Math.min(100.0, (currentStep / (allSteps.length - 1)) * 100.0)

    const renderActiveDot = (key: string) => <Box key={key} className={css.dot} />
    const renderHiddenDot = (key: string) => <Box key={key} className={css.dotHidden} />
    const renderCheckmark = (key: string, step: string, index: number) => (
        <Box className={css.checkBtn} key={key}>
            <button
                type="button"
                className={css.checkmark}
                onFocus={() => setTooltipIndex(index)}
                onBlur={() => setTooltipIndex(-1)}
                onMouseEnter={() => setTooltipIndex(index)}
                onMouseLeave={() => setTooltipIndex(-1)}
                onClick={() => onStepSelected?.(step)}
            >
                <Checkbox
                    width={24}
                    height={24}
                    color={'var(--selectable-selected-background-idle)'}
                />
            </button>

            {tooltipIndex === index && (
                <Text
                    className={css.tooltip}
                    size="body-sm"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized earlier (I hope)
                    dangerouslySetInnerHTML={{ __html: step }}
                />
            )}
        </Box>
    )

    return (
        <Box className={css.container} onMouseLeave={() => setTooltipIndex(-1)}>
            <Box
                className={cn(css.stepper, { [css.twist]: theme === 'twist' }, className)}
                {...rest}
            >
                <Box className={css.bgProgress}>
                    <Box className={css.line} />
                    <Box className={css.goal} />
                </Box>

                <Box className={css.progress}>
                    <Box className={css.progressLine} style={{ width: `${percentProgress}%` }} />
                    {allSteps.map((step, index) => {
                        if (showEnding) {
                            if (index === allSteps.length - 1) {
                                return renderActiveDot(`dot-${step}-${index}`)
                            }

                            if (index >= steps.length) {
                                return renderHiddenDot(`hidden-${step}-${index}`)
                            }

                            return renderCheckmark(`step-${step}-${index}`, step, index)
                        }

                        if (index < steps.length - 1) {
                            return renderCheckmark(`step-${step}-${index}`, step, index)
                        }

                        if (index === currentStep) {
                            return renderActiveDot(`dot-${step}-${index}`)
                        }

                        return renderHiddenDot(`hidden-${step}-${index}`)
                    })}
                </Box>
            </Box>
        </Box>
    )
}
